import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { sort } from 'fast-sort';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  ApiRbacService,
  AppConfig,
  Configuration,
  UserProfileService,
} from 'sigv-library';
import { MenuTree } from 'sigv-library/model';
import { deepCopy, isNullOrEmpty } from 'sigv-library/util';
import { RbacService } from 'src/app/api-info/system-management-api';
import { SYSTEM_GROUP_CODE } from '../constants/global-constants.constant';
import { UseRole } from '../enums/use-role.enum';
import { ApiResponse } from '../models/api-response.model';
import { IceMenuItem } from '../models/ice-menu-item.model';
import { MenuInfo } from '../models/menu-info';
import { TodoListCountResponse } from '../models/todo-list-count-response.model';
import { ApiWebSubmissionService } from './api-web-submission.service';
import { EnvironmentService } from './environment.service';
import { MenuProcessorService } from './menu-processor.service';

@Injectable({
  providedIn: 'root',
})
export class ExtranetMenuService {
  menuItems: MenuInfo[] = [];
  displayMenuItems: MenuInfo[] = [];

  private sidenav$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    true
  );

  private menus$: BehaviorSubject<IceMenuItem[]> = new BehaviorSubject<
    IceMenuItem[]
  >([]);
  private menusExtranet$: BehaviorSubject<MenuInfo[]> = new BehaviorSubject<
    MenuInfo[]
  >([]);

  private todoListCount$: BehaviorSubject<TodoListCountResponse | null> =
    new BehaviorSubject<TodoListCountResponse | null>(null);

  private activeMenuItem?: MenuInfo;
  private todoMenuItem: MenuInfo = this.getTodoMenuItem();

  constructor(
    private logger: NGXLogger,
    private router: Router,
    private rbacService: RbacService,
    private apiRbacService: ApiRbacService,
    private appConfig: AppConfig,
    private configuration: Configuration,
    private apiWebSubmissionService: ApiWebSubmissionService,
    private userProfileService: UserProfileService,
    private menuProcessorService: MenuProcessorService,
    private translateService: TranslateService
  ) {
    // intranet
    if (!this.configuration.isExtranet) {
      this.userProfileService.currentUserSubject
        .asObservable()
        .subscribe((userProfile) => {
          if (!isNullOrEmpty(userProfile) && userProfile?.userNo) {
            this.initMenuData(userProfile.userNo, userProfile.extranetUserRole);
          }
        });
      this.translateService.onLangChange.subscribe(
        (result: LangChangeEvent) => {
          // TODO: 實作切換語系時，menu 資料的更新
          console.log(
            'menu service subscribe translateService onLangChange => ',
            result
          );
        }
      );
    } else {
      // Extranet
      this.userProfileService.currentUserSubject.subscribe({
        next: (result) => {
          if (!result) {
            return;
          }

          const userRole =
            this.userProfileService.currentUserValue?.extranetUserRole || 0;

          if (userRole === Number(UseRole.Dealer)) {
            this.initDealerMenu().subscribe((results) => {
              this.menuItems = results;
              this.translateMenuLabel(
                this.translateService.currentLang,
                this.menuItems
              );
              this.updateDisplayMenu();
            });
          } else if (userRole == Number(UseRole.Agent)) {
            this.initAgentMenu().subscribe((results) => {
              this.menuItems = results;
              this.translateMenuLabel(
                this.translateService.currentLang,
                this.menuItems
              );
              this.updateDisplayMenu();
            });
          } else {
            this.initMenuItems(this.translateService.currentLang);
          }
        },
      });

      this.translateService.onLangChange.subscribe(
        (result: LangChangeEvent) => {
          this.updateTodoMenuI18nLabels();
          if (this.menuItems.length > 0) {
            this.translateMenuLabel(result.lang, this.menuItems);
            this.updateDisplayMenu();
          } else {
            const userRole =
              this.userProfileService.currentUserValue?.extranetUserRole || 0;

            if (userRole === Number(UseRole.Dealer)) {
              this.initDealerMenu().subscribe((results) => {
                this.menuItems = results;
                this.translateMenuLabel(
                  this.translateService.currentLang,
                  this.menuItems
                );
                this.updateDisplayMenu();
                this.logger.info('Menu tree', this.menuItems);
              });
            } else if (userRole == Number(UseRole.Agent)) {
              this.initAgentMenu().subscribe((results) => {
                this.menuItems = results;
                this.translateMenuLabel(
                  this.translateService.currentLang,
                  this.menuItems
                );
                this.updateDisplayMenu();
                this.logger.info('Menu tree', this.menuItems);
              });
            } else {
              this.initMenuItems(this.translateService.currentLang);
            }
          }
        }
      );
    }
  }

  get currentMenuItem(): MenuInfo | undefined {
    return this.activeMenuItem;
  }

  get menuItems$(): Observable<MenuInfo[]> {
    return this.menusExtranet$.asObservable();
  }

  /**
   * 處理 menu api 資料
   *
   * @private
   * @return {*}  {Observable<MenuInfo[]>}
   * @memberof MenuService
   */
  processMenuItemsData(): Observable<MenuInfo[]> {
    const userNo = this.userProfileService.currentUserValue?.userNo;
    if (isNullOrEmpty(userNo)) {
      return of(this.menuItems);
    }

    const observable = new Observable<MenuInfo[]>((observer) => {
      this.rbacService
        .rbacUserProfileUserNoSystemGroupSystemGroupCodeMenuTreeGet(
          SYSTEM_GROUP_CODE,
          userNo!,
          false
        )
        .subscribe({
          next: (results) => {
            const funcMenuItems: MenuInfo[] = [];

            // 第一階層(公司)
            for (const companyMenuItem of results) {
              if ((companyMenuItem.systems ?? []).length === 0) {
                continue;
              }

              const companyId = companyMenuItem.companyId;

              // 第二階層(系統)
              const systems = sort(companyMenuItem.systems ?? []).asc(
                (x) => x.sequence
              );
              for (const systemMenuItem of systems) {
                const systemId = systemMenuItem.systemId;
                var systemHostUrl = systemMenuItem.systemHostUrl;
                var systemCode = systemMenuItem.systemCode;

                // 第三階層(系統功能)
                const sysFunctions = sort(
                  systemMenuItem.systemFunctions ?? []
                ).asc((x) => x.sequence);
                for (const funcMenuItem of sysFunctions) {
                  const systemFunctionId = funcMenuItem.systemFunctionId;
                  const processDefKey = funcMenuItem.processDefKey;
                  const functionCode = funcMenuItem.functionCode;

                  const avaliblePrivilegeItems = sort(
                    funcMenuItem.functionPrivileges ?? []
                  ).asc((x) => x.sequence);

                  if (avaliblePrivilegeItems.length > 0) {
                    const functionMenus: MenuInfo = {
                      data: {
                        systemHostUrl,
                        companyId,
                        systemId,
                        systemFunctionId,
                        systemCode,
                        functionCode,
                        processDefKey,
                      },
                    };

                    // 如果底下功能大於一個就展開功能選單，只有一個的話就直接覆蓋系統功能連結
                    if (avaliblePrivilegeItems.length > 1) {
                      const privilegeMenus: MenuInfo[] = [];
                      functionMenus.srcLabel =
                        funcMenuItem.functionNameEnglish ??
                        funcMenuItem.functionName;
                      functionMenus.i18nLabels =
                        funcMenuItem.functionNameOther ?? [];

                      // 第四階層(功能)
                      for (const privilegeItem of avaliblePrivilegeItems) {

                        if(privilegeItem.refSystem &&
                          !isNullOrEmpty(privilegeItem.refSystem.systemId)){
                          systemHostUrl = privilegeItem.refSystem.systemHostUrl;
                          systemCode = privilegeItem.refSystem.systemCode;
                        }
                        else{
                          systemHostUrl = systemMenuItem.systemHostUrl;
                          systemCode = systemMenuItem.systemCode;
                        }
                        const privilegeCode = privilegeItem.privilegeCode;
                        const itemMenu: MenuInfo = {
                          srcLabel:
                            privilegeItem.privilegeNameEnglish ??
                            privilegeItem.privilegeName,
                          i18nLabels: privilegeItem.privilegeNameOther ?? [],
                          data: {
                            systemHostUrl,
                            companyId,
                            systemId,
                            systemFunctionId,
                            systemCode,
                            functionCode,
                            processDefKey,
                            privilegeCode,
                          },
                        };
                        itemMenu.icon = [
                          systemCode,
                          functionCode,
                          privilegeCode,
                        ].join('_');
                        itemMenu.entryUrl = privilegeItem.entryUrl;

                        privilegeMenus.push(itemMenu);
                      }
                      functionMenus.items = privilegeMenus;
                      functionMenus.icon = [systemCode, functionCode].join('_');
                    } else {
                      const privilegeItem = avaliblePrivilegeItems[0];
                      functionMenus.srcLabel =
                        privilegeItem.privilegeNameEnglish ??
                        privilegeItem.privilegeName;
                      functionMenus.i18nLabels =
                        privilegeItem.privilegeNameOther ?? [];
                      functionMenus.data!.privilegeCode =
                        privilegeItem.privilegeCode;
                      functionMenus!.icon = [
                        systemCode,
                        functionCode,
                        privilegeItem.privilegeCode,
                      ].join('_');
                      functionMenus.entryUrl = privilegeItem.entryUrl;
                    }

                    funcMenuItems.push(functionMenus);
                  }
                }
              }
            }

            observer.next(funcMenuItems);
            observer.complete();
          },
          error: (err) => observer.error(err),
        });
    });

    return observable;
  }
  /**
   * 初始化選單資料
   */
  initMenuItems(lang: string): void {
    this.processMenuItemsData().subscribe((results) => {
      const tmpMenus = results.filter(
        (x) => !x.data?.functionCode?.toLowerCase().startsWith('extodo')
      );
      const webSubmissionMenuIdx = tmpMenus.findIndex((x) =>
        x.data?.functionCode?.toLowerCase().startsWith('exsubmission')
      );
      this.menuItems =
        webSubmissionMenuIdx > -1
          ? [
              ...tmpMenus.slice(0, webSubmissionMenuIdx + 1),
              this.todoMenuItem,
              ...tmpMenus.slice(webSubmissionMenuIdx + 1),
            ]
          : [this.todoMenuItem, ...tmpMenus];
      this.updateTodoMenuI18nLabels();
      this.translateMenuLabel(lang, this.menuItems);
      this.updateDisplayMenu();
    });
  }

  /**
   * Translate menu labels.
   * @param lang 目前語言
   * @param menuItems Target menu items
   */
  translateMenuLabel(lang: string, menuItems: MenuInfo[]): void {
    if (menuItems.length === 0) {
      return;
    }

    menuItems.forEach((x) => {
      x.label =
        x.i18nLabels?.find((y) => y.i18nCode === lang)?.i18nValue ?? x.srcLabel;

      if (isNullOrEmpty(x.url) === false) {
        const urlTree = this.router.parseUrl(
          x.url!.replace(x.data?.systemHostUrl, '')
        );
        if (urlTree.queryParamMap.has('lang')) {
          urlTree.queryParams.lang = lang;
          const routeUrl = this.router.serializeUrl(urlTree);
          x.url = `${x.data?.systemHostUrl}${routeUrl}`;
        }
      }

      this.translateMenuLabel(lang, x.items ?? ([] as MenuInfo[]));
    });
  }

  getMenuItemsObservable(): Observable<IceMenuItem[]> {
    return this.menus$.asObservable();
  }

  getSideMenuObservable(): Observable<boolean> {
    return this.sidenav$.asObservable();
  }

  getTodoListCountObservable(): Observable<TodoListCountResponse | null> {
    return this.todoListCount$.asObservable();
  }

  toggleSideMenu(): void {
    this.sidenav$.next(!this.sidenav$.value);
  }

  private initStaffMenuData(userNo: string): void {
    this.apiRbacService.setBaseUrl(this.appConfig.getApiUrl('rbac'));

    const getMenuTree$ = this.apiRbacService.doGet<MenuTree[]>(
      `/UserProfile/${userNo}/SystemGroup/${this.configuration.systemGroupCode}/MenuTree` +
        '?filterMultipleRole=false'
    );

    getMenuTree$.subscribe((menuTree) => {
      if (menuTree && menuTree.length > 0) {
        const companyMenu = menuTree[0];
        const systemMenu = companyMenu?.systems.find(
          (system) => system.systemCode === 'Web Submission'
        );

        const iceMenus: IceMenuItem[] = [];
        if (systemMenu) {
          systemMenu.systemFunctions
            // 外網不需提供欄位參數設定選單
            .filter(
              ({ functionCode }) =>
                !environment.isExtranet ||
                !['AnnouncementSetting', 'WebSubmissionSetting'].includes(
                  functionCode
                )
            )
            .forEach((systemFunction) => {
              if (
                systemFunction.functionPrivileges &&
                systemFunction.functionPrivileges.length > 0
              ) {
                systemFunction.functionPrivileges.forEach((privilege) => {
                  iceMenus.push({
                    systemCode: systemMenu.systemCode,
                    functionCode: systemFunction.functionCode,
                    privilegeCode: privilege.privilegeCode,
                  });
                });
              } else {
                iceMenus.push({
                  systemCode: systemMenu.systemCode,
                  functionCode: systemFunction.functionCode,
                  privilegeCode: '',
                });
              }
            });
        }

        this.menus$.next(iceMenus);
      } else {
        this.menus$.next([]);
      }
    });
  }

  private initDealerMenu(): Observable<MenuInfo[]> {
    return of(EnvironmentService.environmentConfig.dealerMenu);
  }

  private initAgentMenu(): Observable<MenuInfo[]> {
    return of(EnvironmentService.environmentConfig.agentMenu);
  }

  private initOutsiderMenuData(userRole: number): void {
    // TODO: [MOCK] 目前無供應商選單資料可查詢
    const menus = [
      {
        systemCode: 'Web Submission',
        functionCode: 'Submission',
        privilegeCode: 'Add',
      },
      {
        systemCode: 'Web Submission',
        functionCode: 'Search',
        privilegeCode: 'Search',
      },
      {
        systemCode: 'Web Submission',
        functionCode: 'Report',
        privilegeCode: 'Report',
        subMenus: [
          {
            systemCode: 'Web Submission',
            functionCode: 'Report',
            privilegeCode: 'Approval',
            icon: 'ic-report.svg',
            label: 'Approval',
            routerLinkPath: '/report/approval',
            translateKey: 'Menu.Approval',
          },
          {
            systemCode: 'Web Submission',
            functionCode: 'Report',
            privilegeCode: 'SalesTargetCompletionBriefing',
            icon: 'ic-report.svg',
            label: 'Sales Target Completion Briefing',
            routerLinkPath: '/report/sales-target-completion-briefing',
            translateKey: 'Menu.SalesTargetCompletionBriefing',
            onlyUseRole: 0,
          },
          {
            systemCode: 'Web Submission',
            functionCode: 'Report',
            privilegeCode: 'SalesPerformanceReport',
            icon: 'ic-report.svg',
            label: 'Sales Performance Report',
            routerLinkPath: '/report/sales-performance-report',
            translateKey: 'Menu.SalesPerformanceReport',
            onlyUseRole: 0,
          },
        ],
      },
      {
        systemCode: 'Web Submission',
        functionCode: 'Insurance',
        privilegeCode: 'Insurance',
      },
      {
        systemCode: 'Web Submission',
        functionCode: 'Announcement',
        privilegeCode: 'Enquire',
      },
    ].filter((menu) => userRole === 1 || menu.privilegeCode !== 'Insurance');

    this.menus$.next(menus);
  }

  initMenuData(userNo: string, userRole = 0): void {
    const outsiderRoleConditions = [1, 2];
    if (outsiderRoleConditions.includes(userRole)) {
      this.initOutsiderMenuData(userRole);
    } else {
      this.initStaffMenuData(userNo);
    }
  }

  clearMenus(): void {
    this.menus$.next([]);
  }

  updateTodoListCount(): void {
    if (this.configuration.isExtranet) {
      this.updateDisplayMenu();
    } else {
      const useRole =
        this.userProfileService.currentUserValue?.extranetUserRole;
      this.apiWebSubmissionService
        .doGet<ApiResponse<TodoListCountResponse>>(
          `/Common/TodoListCount`,
          undefined,
          { ['X-UseRole']: useRole?.toString() || '' }
        )
        .subscribe(
          (response) => {
            if (response && response.rtnCode === 0 && response.data) {
              this.todoListCount$.next(response.data);
            } else {
              this.todoListCount$.next(this.getDefaultTodoListCountObject());
            }
          },
          () => {
            this.todoListCount$.next(this.getDefaultTodoListCountObject());
          }
        );
    }
  }

  private getTodoMenuItem(): MenuInfo {
    const todoSrcLabel = 'Menu.To Do';
    return {
      srcLabel: todoSrcLabel,
      icon: 'ic-todo',
      data: {
        functionCode: 'ExTodo',
        privilegeCode: 'ExTodo',
      },
    };
  }

  /**
   * 更新目前語言的語言庫
   */
  private updateTodoMenuI18nLabels(): void {
    this.todoMenuItem.i18nLabels = [
      {
        i18nCode: this.translateService.currentLang,
        i18nValue: this.translateService.instant(this.todoMenuItem.srcLabel!),
      },
    ];
  }

  private getDefaultTodoListCountObject(): TodoListCountResponse {
    return {
      fieldSettingDraftCount: 0,
      fieldSettingApplyCount: 0,
      fieldSettingApproveCount: 0,
      fieldSettingWithdrawReviewCount: 0,
      draftCount: 0,
      submissionCount: 0,
      rejectCount: 0,
      announcememtApplyCount: 0,
      announcementApproveCount: 0,
      agreementPrepTodoCount: 0,
      signDeliveryOrderTodoCount: 0,
    };
  }

  updateDisplayMenu(): void {
    const tmpMenuItems = deepCopy(this.menuItems);
    this.menuProcessorService
      .prettfyMenus(tmpMenuItems!)
      .subscribe((newMenuItems) => {
        this.displayMenuItems = newMenuItems;
        this.menusExtranet$.next(this.displayMenuItems);
      });
  }
}
